import frauncesLatin from '@fontsource-variable/fraunces/files/fraunces-latin-standard-normal.woff2';
import interLatin from '@fontsource-variable/inter/files/inter-latin-wght-normal.woff2';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import useDocumentThemeColor from '@/components/Html/useDocumentThemeColor/useDocumentThemeColor';
import { getConfig } from '@/constants/config';
import { environment } from '@/utils/env.utils';

// Used to cache bust the favicon and og image in case it changes.
// Source: https://stackoverflow.com/questions/2208933/how-do-i-force-a-favicon-refresh/7116701#7116701
const FAVICON_VERSION = 3;
const OG_IMAGE_VERSION = 3;

const getBrandingBasePath = () => `/branding/${getConfig().country.toLowerCase()}`;

const getFaviconSvgFilename = () => {
  if (environment.env === 'production') {
    return 'favicon.svg';
  }
  return `favicon-${environment.env}.svg`;
};

const Html = () => {
  const { t } = useTranslation('Metadata');
  const location = useLocation();
  const themeColor = useDocumentThemeColor();

  const title = t('title');
  const description = t('description');
  const basePath = getBrandingBasePath();
  const ogImageUrl = `${window.location.origin}${basePath}/og-image.png?v=${OG_IMAGE_VERSION}`;

  useEffect(() => {
    document.documentElement.lang = getConfig().locale;
  }, []);

  return (
    <>
      {/* Preload fonts to avoid shift when fonts are loaded */}
      <link rel="preload" href={interLatin} as="font" type="font/woff2" crossOrigin="anonymous" />
      <link rel="preload" href={frauncesLatin} as="font" type="font/woff2" crossOrigin="anonymous" />

      <meta name="ui-version" content={environment.version} />

      {/* General */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="theme-color" content={themeColor} />

      <link rel="manifest" href={`${basePath}/manifest.webmanifest`} />
      <link rel="icon" href={`${basePath}/favicon.ico?v=${FAVICON_VERSION}`} sizes="32x32" />
      <link
        rel="icon"
        href={`${basePath}/${getFaviconSvgFilename()}?v=${FAVICON_VERSION}`}
        type="image/svg+xml"
      />
      <link rel="apple-touch-icon" href={`${basePath}/apple-touch-icon.png?v=${FAVICON_VERSION}`} />

      {/* Open Graph / Facebook */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={`${window.location.origin}${location.pathname}`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="699" />
      <meta property="og:image:height" content="324" />

      {/* Twitter */}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:site" content="@fairlosweden" />

      {environment.env !== 'production' ? <meta name="robots" content="noindex,nofollow" /> : null}
    </>
  );
};

export default Html;
